import React, { createContext, useContext, useState, useEffect } from "react";
import { api } from "../lib/utils";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const userData = await api.get("/auth/me");
      setUser(userData);
    } catch (err) {
      localStorage.removeItem("token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await api.post("/auth/login", { email, password });
      localStorage.setItem("token", response.access_token);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const register = async (name, email, password, orgName, inviteToken, companyType) => {
    setError(null);
    try {
      const body = { name, email, password };
      if (orgName) body.org_name = orgName;
      if (inviteToken) body.invite_token = inviteToken;
      if (companyType) body.company_type = companyType;
      const response = await api.post("/auth/register", body);
      localStorage.setItem("token", response.access_token);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  const isAdmin = user?.role === "admin";
  const isAccountant = user?.role === "accountant" || isAdmin;
  const canEdit = isAccountant;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        isAdmin,
        isAccountant,
        canEdit,
        fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
